import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionGroup, Button, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import {
  AEMContentProvider,
  AemFeature,
  createAemUrl,
  SKIP_PARAMETER_CONSTANT,
} from '@app/components/AdobeExperienceFragments';
import { PlanChangePendingBadge } from '@app/components/PlanChangePendingBadge';
import {
  useCanChangePlan,
  useHasPlanChangePending,
  useScreenResolution,
  useShouldShowClickToDisconnect,
} from '@app/hooks';
import { refetchCurrentAddOns, selectAddOns } from '@app/redux/addOns';
import { selectUser } from '@app/redux/auth';
import {
  getBroadbandLabelAttributes,
  selectBroadbandLabelAttributes,
} from '@app/redux/broadbandLabel/broadbandLabelSlice';
import { selectChangePlan } from '@app/redux/changePlan';
import { selectConfig } from '@app/redux/config';
import { selectRetentionOffers } from '@app/redux/disconnect';
import { selectLocale } from '@app/redux/locale';
import { getPendingInstallation, selectingPendingInstallation } from '@app/redux/pendingInstallation';
import { getCharacteristics, getPrice, selectCurrentPlanPrice, selectPlanCharacteristics } from '@app/redux/plan';
import { getPortfolio, selectPortfolio } from '@app/redux/portfolio';
import { selectUserInfo } from '@app/redux/userInfo';
import { isVoiceOnlyPlan } from '@app/utils';

import { AccountType, Locale, ProductInstanceStatus } from '@mfe/shared/schema-types';

import { useShouldRefetchCurrentAddons } from '../../../ShopAddons/hooks';
import { ManagePlanModal } from '../ManagePlanModal';
import { AddonsCard } from './AddonsCard';
import { Characteristics } from './Characteristics';
import { EquipmentShippingAlert } from './EquipmentShippingAlert';
import { PlanName } from './PlanName';
import { Price } from './Price';
import { ScheduledAlert } from './ScheduledAlert';
import { UnscheduledAlert } from './UnscheduledAlert';
import { ProductInstanceTypes } from '../../../../../../platform/src/server/dataSources/PSM/types';

import bodyStyles from '../styles.module.scss';
import loadingStyles from './Loading.module.scss';
import styles from './styles.module.scss';

type PlanAndAddonsTabProps = {
  navigateToChangePlan: () => void;
  navigateToScheduleInstallation: () => void;
};

export const PlanAndAddonsTab = ({ navigateToChangePlan, navigateToScheduleInstallation }: PlanAndAddonsTabProps) => {
  const dispatch = useDispatch();

  const shouldRefetchCurrentAddOns = useShouldRefetchCurrentAddons();
  const { addOnOrderId } = useSelector(selectAddOns);

  const {
    user: { productInstanceStatus },
  } = useSelector(selectUser);
  const { portfolio } = useSelector(selectPortfolio);
  const {
    characteristics: { id: productTypeId },
  } = useSelector(selectPlanCharacteristics);
  const { price } = useSelector(selectCurrentPlanPrice);

  const [isModalOpen, setIsModalOpen] = React.useState(false);

  React.useEffect(() => {
    if (shouldRefetchCurrentAddOns) {
      dispatch(refetchCurrentAddOns(addOnOrderId as string));
    }
    dispatch(getPendingInstallation());
    dispatch(getBroadbandLabelAttributes());
  }, []);

  React.useEffect(() => {
    if (!portfolio) dispatch(getPortfolio());
  }, [dispatch, portfolio]);

  React.useEffect(() => {
    if (!productTypeId) dispatch(getCharacteristics());
  }, [dispatch, productTypeId]);

  React.useEffect(() => {
    if (!price) dispatch(getPrice());
  }, [dispatch, price]);

  const canAccessDeactivation = productInstanceStatus !== ProductInstanceStatus.Deactivated;
  const openManagePlanModal = () => (canAccessDeactivation ? setIsModalOpen(true) : null);

  return (
    <div className={styles['rightSection']}>
      <PlanInfo
        navigateToChangePlan={navigateToChangePlan}
        navigateToScheduleInstallation={navigateToScheduleInstallation}
        openManagePlanModal={openManagePlanModal}
      />
      <AddonsCard />
      {canAccessDeactivation && (
        <ManagePlanModal
          isOpen={isModalOpen}
          handleCloseModal={() => setIsModalOpen(false)}
          navigateToChangePlan={navigateToChangePlan}
        />
      )}
    </div>
  );
};

interface PlanInfoProps extends PlanAndAddonsTabProps {
  openManagePlanModal: () => void;
}

const PlanInfo = ({ navigateToChangePlan, navigateToScheduleInstallation, openManagePlanModal }: PlanInfoProps) => {
  const { t } = useTranslation(['Profile', 'Global']);
  const { isSmall, isExtraSmall } = useScreenResolution();

  const { loading: characteristicsLoading } = useSelector(selectPlanCharacteristics);
  const { loading: priceLoading } = useSelector(selectCurrentPlanPrice);
  const hasDataLoaded = !characteristicsLoading && !priceLoading;

  const showMissingInfoNotice = useShowMissingInfoNotice();
  const { hasPlanChangePending, hasPendingTransitionLoading } = useHasPlanChangePending();

  return (
    <Surface
      className={styles['planInfo']}
      data-cy="internet-plan-card-surface"
      variant="primary"
      radius={isSmall || isExtraSmall ? '0px' : '16px'}
    >
      <div className={bodyStyles['cardTitle']}>
        <Txt variant="heading5" component="span" color="regular">
          {t('Profile:myPlanCardTitle')}
        </Txt>
        <div className={styles['badgeAndButton']}>
          {hasPlanChangePending && !hasPendingTransitionLoading && <PlanChangePendingBadge />}
          <ActionButton navigateToChangePlan={navigateToChangePlan} openManagePlanModal={openManagePlanModal} />
        </div>
      </div>
      <Surface pt="24px" px={{ xs: '16px', sm: '24px' }} className={styles['cardBody']}>
        <PendingInstallationAlert navigateToScheduleInstallation={navigateToScheduleInstallation} />
        {hasDataLoaded && showMissingInfoNotice && (
          <Txt variant="bodySmallItalic" color="subtle">
            {t('InternetPlan.missingPlanInfo')}
          </Txt>
        )}
        <div className={bodyStyles['planHeader']} data-cy="planDetails">
          <PlanName />
          <Price />
        </div>
        <div className={bodyStyles['attributesContainer']}>
          <Characteristics />
        </div>
        <AccordionGroup style={{ width: '100%' }}>
          <Accordion
            className={bodyStyles['broadbandFactsAccordion']}
            title={<Txt variant="labelMedium">{t('InternetPlan.broadbandFacts')}</Txt>}
          >
            <BroadbandLabel />
          </Accordion>
        </AccordionGroup>
      </Surface>
    </Surface>
  );
};

const ActionButton = ({
  openManagePlanModal,
  navigateToChangePlan,
}: {
  openManagePlanModal: () => void;
  navigateToChangePlan: () => void;
}) => {
  const { t } = useTranslation(['Profile', 'Global', 'Overview']);
  const canChangePlan = useCanChangePlan();

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const showClickToDisconnect = useShouldShowClickToDisconnect();

  const { hasActivationError, loading: planOffersLoading } = useSelector(selectChangePlan);

  const { loading: alertLoading, hasPendingInstallation } = useSelector(selectingPendingInstallation);

  const { loading: retentionOffersLoading } = useSelector(selectRetentionOffers);

  const loading = planOffersLoading || retentionOffersLoading;

  const {
    user: { productKind, productInstanceStatus },
  } = useSelector(selectUser);

  const isDeactivated = productInstanceStatus === ProductInstanceStatus.Deactivated;

  const showChangePlanButton =
    !isDeactivated && (userLocale === Locale.EnUs || accountType === AccountType.Residential);

  const isChangePlanDisabled =
    productKind === ProductInstanceTypes.WirelessInternet ||
    !canChangePlan ||
    (hasPendingInstallation && alertLoading) ||
    hasActivationError;

  const managePlanButtonProps = {
    'data-cy': 'manage-plan-button',
    onClick: openManagePlanModal,
    disabled: false,
    label: t('Overview:planPreview.managePlan'),
  };

  const changePlanButtonProps = {
    'data-cy': 'change-plan-button',
    onClick: navigateToChangePlan,
    disabled: isChangePlanDisabled,
    label: t('Usage:buttons.changePlan'),
  };

  const cardButtonProps = showClickToDisconnect ? managePlanButtonProps : changePlanButtonProps;

  const button = showChangePlanButton ? (
    <Button buttonSize="small" variant="secondary" {...cardButtonProps}>
      {cardButtonProps.label}
    </Button>
  ) : null;

  return loading ? <div className={loadingStyles['buttonLoading']} /> : button;
};

const PendingInstallationAlert = ({
  navigateToScheduleInstallation,
}: {
  navigateToScheduleInstallation: () => void;
}) => {
  const {
    loading: alertLoading,
    hasPendingInstallation,
    schedule,
    isEquipmentShipped,
  } = useSelector(selectingPendingInstallation);

  const handleRedirect = () => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
    navigateToScheduleInstallation();
  };

  return alertLoading ? (
    <div className={loadingStyles['alertLoading']} />
  ) : (
    hasPendingInstallation &&
      (schedule ? (
        <ScheduledAlert handleRedirect={handleRedirect} />
      ) : (
        <UnscheduledAlert handleRedirect={handleRedirect} />
      )) &&
      isEquipmentShipped && <EquipmentShippingAlert />
  );
};

const useShowMissingInfoNotice = () => {
  const {
    characteristics: { id: productTypeId, name: planName, uploadSpeed, isUnleashed, downloadSpeed },
  } = useSelector(selectPlanCharacteristics);

  const { price, errors: priceError } = useSelector(selectCurrentPlanPrice);

  const hasVoicePlan = isVoiceOnlyPlan(productTypeId);

  if (hasVoicePlan) {
    return Boolean(priceError);
  }

  const attributes = isUnleashed ? [planName, price] : [planName, price, downloadSpeed, uploadSpeed];

  return attributes.some((attr) => !attr);
};

const BroadbandLabel = () => {
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const {
    data: { upi, serviceAreaId, serviceId, satelliteName, iduDeviceModel },
    loading,
  } = useSelector(selectBroadbandLabelAttributes);
  const { aemBaseUrl } = useSelector(selectConfig);

  return !loading ? (
    <AEMContentProvider
      src={createAemUrl({
        baseUrl: aemBaseUrl,
        locale: userLocale,
        feature: AemFeature.PLAN_LABEL,
        accountType,
        additionalSelectors: ['pidi'],
        suffixes: {
          upi,
          serviceAreaId,
          offerId: SKIP_PARAMETER_CONSTANT,
          satelliteName,
          serviceId,
          iduDeviceModel,
        },
      })}
      id={AemFeature.PLAN_LABEL}
      style={{ textAlign: 'left' }}
    />
  ) : null;
};
